import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';

import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useAppSelector } from 'js/store/hooks';
import { useAfterLoginAction } from './useAfterLoginAction';
import { useFailedLoginAction } from './useFailedLoginAction';
import { useSuccessLoginAction } from './useSuccessLoginAction';
import { useGetWaitingMessages } from './useGetWaitingMessages';
import { useLoginBySessionToken } from './useLoginBySessionToken';
import { useRedirectToAfterLoginURL } from './useRedirectToAfterLoginURL';
import { useGetCredentialsLoginHooks } from './useGetCredentialsLoginHooks';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useAfterCredentialsLoginAction } from './useAfterCredentialsLoginAction';

export const useContinueLoginWithSessionToken = () => {
    const dispatch = useDispatch();
    const afterLoginAction = useAfterLoginAction();
    const failedLoginAction = useFailedLoginAction();
    const successLoginAction = useSuccessLoginAction();
    const getWaitingMessages = useGetWaitingMessages();
    const loginBySessionToken = useLoginBySessionToken();
    const redirectToAfterLoginURL = useRedirectToAfterLoginURL();
    const getCredentialsLoginHooks = useGetCredentialsLoginHooks();
    const afterCredentialsLoginAction = useAfterCredentialsLoginAction();
    const oapiLoginTimeout = useAppSelector(prop('oapiLoginTimeout'));

    return useCallback(
        async ({
            userId,
            userName,
            password,
            loginName,
            sessionToken,
            isAutoLogin,
            afterRegisterLogin,
            isLoginToNativeAppEnabled,
            isAfterLoginWithCredentials
        }: {
            afterRegisterLogin?: boolean;
            isAfterLoginWithCredentials?: boolean;
            isAutoLogin?: boolean;
            isLoginToNativeAppEnabled?: boolean;
            languageCode: string;
            loginName?: string;
            password: string;
            realMode?: 0 | 1;
            rememberMeLogin?: boolean;
            sessionToken: string;
            userId?: string;
            userName: string;
        }) => {
            if (isLoginToNativeAppEnabled) {
                try {
                    await helpers.externalLoginAction({
                        userId, loginName, userName, sessionToken, password, afterRegisterLogin
                    });
                } catch (error) {
                    return Promise.reject(error);
                }
            }

            try {
                const isLoginTimeoutNeeded = oapiLoginTimeout > 0
                    && isAfterLoginWithCredentials
                    && !afterRegisterLogin;

                const abortController = new AbortController();
                const { signal } = abortController;

                await Promise.race([
                    (async () => {
                        await loginBySessionToken(userName, sessionToken, { signal });

                        if (isAfterLoginWithCredentials) {
                            await getCredentialsLoginHooks();
                        }

                        await afterLoginAction({ signal });
                    })(),
                    ...isLoginTimeoutNeeded ? [new Promise((_, reject) => {
                        window.setTimeout(() => {
                            abortController.abort('Request timed out');

                            reject({
                                reason: 'Request timed out',
                                response: {
                                    ID: -1,
                                    data: {
                                        customError: 'playtech.mobile.system.login.error.timeout'
                                    }
                                }
                            });
                        }, oapiLoginTimeout);
                    })] : []
                ]);

                await successLoginAction({ userName, authType: 'credentials' });

                if (isAfterLoginWithCredentials) {
                    publicEventsAPIService.trigger('auth:credentials:login');

                    afterCredentialsLoginAction();
                }

                getWaitingMessages();
                redirectToAfterLoginURL(afterRegisterLogin);

                helpers.storeRememberMeItem(userName);
            } catch (error) {
                failedLoginAction(error);

                throw error;
            } finally {
                dispatch(actions.setIsLoginInProcess(false));
                isAutoLogin && dispatch(actions.setAutoLoginCompleted());
            }
        },
        [
            afterCredentialsLoginAction,
            afterLoginAction,
            dispatch,
            failedLoginAction,
            getCredentialsLoginHooks,
            getWaitingMessages,
            loginBySessionToken,
            oapiLoginTimeout,
            redirectToAfterLoginURL,
            successLoginAction
        ]
    );
};
